import { pagesQueriesLink, urlInspectionLink } from '../GscTools/links';
import { reportsPosition, rowMetrics } from '../GscTools/metrics';
import { orderRows } from '../PagesQueriesChanges/rows';
import { CONTRIBUTORS } from './scope';
import { filterLabel, otherTopicsFor, topicFilters } from './topics';

/**
 * The figures the blocks draw, derived from what came back rather than fetched again, plus the
 * links that carry one exact row into the tool that answers the next question about it.
 */

/**
 * Block 02's rows: one per topic that came back, each against its own baseline.
 *
 * `comparable` is set only where both windows returned the topic. A window that returned nothing
 * is unknown for that topic, not zero, and a baseline of zero clicks has an absolute change but no
 * percentage. There is no total row: topics can overlap, and their sum is not the property's
 * traffic in any case.
 */
export function comparisonRows(scope, totals) {
    return scope.topics
        .filter((topic) => totals[topic.name]?.status === 'done')
        .map((topic) => {
            const current = totals[topic.name].current;
            const prior = totals[topic.name].baseline;
            const comparable = !!current && !!prior;

            return {
                id: topic.name,
                key: topic.name,
                // Carried on the row so the export says which filters produced it.
                filters: topicFilters(topic).map(filterLabel),
                current,
                prior,
                comparable,
                deltaClicks: comparable ? current.clicks - prior.clicks : null,
                deltaPercent:
                    comparable && prior.clicks > 0
                        ? (100 * (current.clicks - prior.clicks)) / prior.clicks
                        : null,
            };
        });
}

/** Block 02's rows in the applied mode's order, through the same ordering Pages & Queries uses. */
export function orderedComparisonRows(scope, totals) {
    return orderRows(comparisonRows(scope, totals), { mode: scope.mode, metric: scope.metric });
}

/**
 * Block 04's rows, highest first in the applied metric. A query row names the other topics it
 * also meets; a page row has no such column, because a page is not a member of a query topic —
 * it is where the topic's queries landed.
 */
export function contributorRows(scope, rows, topicName, view) {
    const hasPosition = reportsPosition(scope.searchType);
    const metric = scope.metric;

    return (rows ?? [])
        .map((row) => {
            const key = row?.keys?.[0] ?? '';
            return {
                id: key,
                key,
                ...rowMetrics(row, hasPosition),
                alsoIn:
                    view === CONTRIBUTORS.QUERIES
                        ? otherTopicsFor(key, scope.topics, topicName)
                        : null,
            };
        })
        .sort((a, b) => b[metric] - a[metric] || (a.key < b.key ? -1 : 1));
}

/**
 * The exact-filtered page contributors of one query: Pages, Queries & Changes on its pages view
 * under a single `query equals` rule, the query exactly as Search Console returned it.
 */
export function queryPagesLink(scope, queryText, period) {
    return pagesQueriesLink(scope, period, {
        view: 'pages',
        rules: [{ dimension: 'query', operator: 'equals', expression: queryText }],
    });
}

/**
 * One page's queries within the topic: Pages, Queries & Changes on its queries view, under a
 * `page equals` rule for the URL and the topic's own filters, so the queries listed there are the
 * topic's queries on that page rather than every query the page received.
 */
export function pageQueriesLink(scope, url, period, topic) {
    return pagesQueriesLink(scope, period, {
        view: 'queries',
        rules: [{ dimension: 'page', operator: 'equals', expression: url }, ...topicFilters(topic)],
    });
}

export function inspectionLink(scope, url) {
    return urlInspectionLink(scope.siteUrl, url);
}
